import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Statistic, Spin, Alert, theme } from 'antd';
import {
  IconUsers,
  IconHomeStar,
  IconMapPin,
  IconCalendarEvent,
  IconFlag,
  IconBuildingBank,
  IconShieldLock,
  IconShieldOff,
  IconWallet,
  IconAlertTriangle,
  IconCurrencyEuro,
  IconTransferOut,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminDashboardSummary, IAdminDashboardSummary } from '../../../services/AdminService';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { CORAL } from '../../../theme/creamyTokens';

const { Title } = Typography;

// KPI cards computed live from data this app's own DbContext can reach --
// see AdminDashboardController's own doc comment for why "usuarios activos"
// is scoped the way it is for now.
const DashboardSection: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [summary, setSummary] = useState<IAdminDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getAdminDashboardSummary()
      .then(setSummary)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    );
  }

  if (error || !summary) {
    return <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_DASHBOARD_LOAD_ERROR)} />;
  }

  const euro = (amount: number) => `${amount.toFixed(2)} €`;

  // Money first. This panel used to count users, listings and bookings and
  // not one euro, which is the dashboard of a booking app rather than of a
  // business that holds other people's money.
  const cards = [
    { icon: <IconBuildingBank size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_IN_ESCROW, value: euro(summary.inEscrow), hint: summary.inEscrowCount },
    { icon: <IconCurrencyEuro size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_FEES_THIS_MONTH, value: euro(summary.platformFeesThisMonth) },
    { icon: <IconShieldLock size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_DEPOSITS_HELD, value: euro(summary.depositsHeld), hint: summary.depositsHeldCount },
    // Machines out there right now with nothing frozen behind them. Alarming
    // on purpose when it isn't zero.
    {
      icon: <IconShieldOff size={20} />,
      labelKey: TRANSLATIONS.ADMIN_DASHBOARD_DEPOSITS_EXPIRED,
      value: summary.depositsExpiredWhileRunning,
      alarming: summary.depositsExpiredWhileRunning > 0,
    },
    { icon: <IconWallet size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_HOST_DEPOSITS, value: euro(summary.hostDepositsBalance) },
    {
      icon: <IconAlertTriangle size={20} />,
      labelKey: TRANSLATIONS.ADMIN_DASHBOARD_HOSTS_LOW,
      value: summary.hostDepositsLowCount,
      alarming: summary.hostDepositsLowCount > 0,
    },
    {
      icon: <IconTransferOut size={20} />,
      labelKey: TRANSLATIONS.ADMIN_DASHBOARD_STUCK_TRANSFERS,
      value: euro(summary.stuckTransfers),
      hint: summary.stuckTransfersCount,
      alarming: summary.stuckTransfersCount > 0,
    },

    { icon: <IconUsers size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_ACTIVE_USERS, value: summary.activeUsers },
    { icon: <IconHomeStar size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_HOSTS, value: summary.hosts },
    { icon: <IconMapPin size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_PUBLISHED_SPACES, value: summary.publishedSpaces },
    { icon: <IconCalendarEvent size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_BOOKINGS_THIS_MONTH, value: summary.bookingsThisMonth },
    { icon: <IconFlag size={20} />, labelKey: TRANSLATIONS.ADMIN_DASHBOARD_UNRESOLVED_REPORTS, value: summary.unresolvedReports },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_DASHBOARD)}
      </Title>

      <Row gutter={[16, 16]}>
        {cards.map((card) => (
          <Col key={card.labelKey} xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {card.icon}
                    {translate(t, card.labelKey)}
                  </div>
                }
                value={card.value}
                valueStyle={card.alarming ? { color: CORAL } : undefined}
                suffix={
                  card.hint != null ? (
                    <span style={{ fontSize: 13, color: token.colorTextTertiary }}>· {card.hint}</span>
                  ) : undefined
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default DashboardSection;
